"use client"

import {useId, useState} from "react";
import {useLocale, useTranslations} from "next-intl";
import {Link} from "@/i18n/navigation";
import {cn} from "@/lib/utils";
import {useZendeskWidget} from "@/hooks/useZendeskWidget";
import {CASINO_FOOTER_LINKS, getSocialLinks} from "@/lib/utils/contants/footerLinks";
import {HeadsetSvg} from "@/app/components/icons/HeadsetSvg";
import {ShieldCheckSvg} from "@/app/components/icons/ShieldCheckSvg";
import {ArrowRightSvg} from "@/app/components/icons/ArrowRightSvg";

// Container-query breakpoints, see AppFooter: 640 = tablet, 1100 = desktop.
// On tablet the four cards are all 164px tall (V2 spec §1).
const CARD = 'rounded-[20px] border border-card-border bg-card-surface';
const CARD_TITLE = 'text-text-strong font-semibold';
const CARD_COPY = 'text-text-muted text-[13px] leading-[18px]';

export const SupportCard = () => {
    const t = useTranslations();
    const {onOpenWidget} = useZendeskWidget();

    return (
        <section className={cn(CARD, 'flex flex-col gap-[10px] p-[16px] @min-[640px]:p-[24px] @min-[640px]:h-[164px]')}>
            <h2 className={cn(CARD_TITLE, 'flex items-center gap-[10px] text-[18px] leading-[25px]')}>
                <HeadsetSvg/>
                {t('AppFooter.supportTitle')}
            </h2>
            <p className={cn(CARD_COPY, 'hidden @min-[640px]:block')}>{t('AppFooter.supportCopy')}</p>
            <button
                type={'button'}
                onClick={onOpenWidget}
                className={'h-[40px] w-full rounded-[12px] border border-[#494134] bg-[#292824] text-gold-soft font-semibold text-[14px] leading-[20px] transition-colors hover:bg-[#34322c] cursor-pointer'}
            >
                {t('AppFooter.contactSupport')}
            </button>
        </section>
    );
};

export const SocialCard = () => {
    const t = useTranslations();
    const locale = useLocale();

    return (
        <section aria-label={t('AppFooter.socialTitle')}
                 className={cn(CARD, 'p-[16px] @min-[640px]:p-[24px] @min-[640px]:h-[164px] @min-[1100px]:h-auto @min-[1100px]:flex-1')}>
            {/* Phones get the links row only. */}
            <h2 className={cn(CARD_TITLE, 'hidden @min-[640px]:block text-[18px] leading-[24px]')}>{t('AppFooter.socialTitle')}</h2>
            <p className={cn(CARD_COPY, 'hidden @min-[640px]:block mt-[6px]')}>{t('AppFooter.socialCopy')}</p>
            <ul className={'flex gap-[8px] @min-[640px]:mt-[6px] @min-[1100px]:gap-[10px]'}>
                {getSocialLinks(locale).map(({label, href, Icon, iconOnly}) => (
                    <li key={href} className={cn(!iconOnly && 'flex-1 max-w-[130px]')}>
                        <a
                            href={href}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            aria-label={iconOnly ? t(label) : undefined}
                            className={cn(
                                'flex items-center justify-center gap-[8px] h-[48px] rounded-[12px] border border-[#363533] bg-[#242424] transition-colors hover:bg-[#2d2d2d]',
                                iconOnly ? 'w-[48px] @min-[640px]:@max-[1099px]:w-[44px]' : 'w-full',
                            )}
                        >
                            <Icon/>
                            {iconOnly ? null : (
                                <span className={'text-text-strong font-medium text-[13px] leading-[18px]'}>{t(label)}</span>
                            )}
                        </a>
                    </li>
                ))}
            </ul>
        </section>
    );
};

// Collapsible on phones (closed by default, a second tap closes it again); always
// open from the tablet layout up.
export const CasinoLinksCard = () => {
    const t = useTranslations();
    const [isOpen, setIsOpen] = useState(false);
    const listId = useId();

    return (
        <nav aria-labelledby={`${listId}-title`}
             className={cn(CARD, 'px-[16px] @min-[640px]:p-[24px] @min-[640px]:h-[164px] @min-[1100px]:h-[140px]')}>
            <h2 id={`${listId}-title`} className={cn(CARD_TITLE, 'text-[16px] leading-[22px]')}>
                <button
                    type={'button'}
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    onClick={() => setIsOpen(open => !open)}
                    className={'flex w-full h-[54px] items-center justify-between cursor-pointer @min-[640px]:hidden'}
                >
                    {t('AppFooter.casinoTitle')}
                    <span aria-hidden className={'text-text-muted text-[22px] leading-[28px] font-normal'}>{isOpen ? '−' : '+'}</span>
                </button>
                <span className={'hidden @min-[640px]:inline'}>{t('AppFooter.casinoTitle')}</span>
            </h2>
            <ul
                id={listId}
                className={cn(
                    'grid-cols-3 grid-flow-col grid-rows-2 gap-x-[12px] gap-y-[10px] pb-[16px] @min-[640px]:pb-0 @min-[640px]:mt-[14px] @min-[1100px]:gap-x-[24px]',
                    isOpen ? 'grid' : 'hidden @min-[640px]:grid',
                )}
            >
                {CASINO_FOOTER_LINKS.map(({label, href}) => (
                    <li key={href}>
                        <Link prefetch={false} href={href}
                              className={cn(CARD_COPY, 'transition-colors hover:text-text-strong')}>
                            {t(label)}
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

// Direct link to the single rules & policies page (/docs hub).
export const RulesCard = () => {
    const t = useTranslations();

    return (
        <section className={cn(CARD, 'relative flex items-center justify-between h-[56px] px-[16px]',
            '@min-[640px]:block @min-[640px]:h-[164px] @min-[640px]:p-[24px] @min-[1100px]:h-[140px]')}>
            <h2 className={cn(CARD_TITLE, 'flex items-center gap-[10px] text-[16px] leading-[22px]')}>
                <ShieldCheckSvg/>
                {t('AppFooter.rulesTitle')}
            </h2>
            <p className={cn(CARD_COPY, 'hidden @min-[640px]:block mt-[10px]')}>{t('AppFooter.rulesCopy')}</p>
            <Link
                prefetch={false}
                href={'/docs'}
                aria-label={t('AppFooter.rulesTitle')}
                className={'flex items-center gap-[8px] text-gold-soft font-semibold text-[13px] leading-[18px] @min-[640px]:mt-[10px] @min-[640px]:w-fit hover:brightness-110 after:absolute after:inset-0 @min-[640px]:after:hidden'}
            >
                <span className={'hidden @min-[640px]:inline'}>{t('AppFooter.rulesLink')}</span>
                <ArrowRightSvg/>
            </Link>
        </section>
    );
};
